import React, { useMemo } from 'react';

const MAX_TRANSIT_DAYS = 5;

const fillWidthStyle = (percent) => {
  if (percent <= 0) return '0%';
  if (percent >= 100) return '100%';

  // Browser zoom/fractional track widths can leave a 1px neutral seam exactly
  // at the fill boundary. A tiny visual overlap removes the seam while keeping
  // the functional range value/thumb unchanged.
  return `calc(${percent}% + 1px)`;
};

/**
 * Planning visibility control — discrete index-based slider (operator spec
 * 2026-07-01, subtask 869dxzmum).
 *
 * The main deadlines axis is now a DISCRETE <input type=range> steered by
 * stepIndex (0 = most urgent / leftmost, stepCount-1 = show-all / rightmost).
 * Every increment changes exactly one cohort on the map — no dead zones, no
 * skips. The number of slider positions equals the number of real cohorts plus
 * one (the show-all sentinel), so a tier with a single order gets exactly ONE
 * position, not a whole segment.
 *
 * Tick geometry (tierTicks from tierStepTicks) uses pre-computed percent values
 * derived from index / (stepCount - 1) * 100, so all ticks are equidistant and
 * the fill width is always index-aligned.
 *
 * Active tick: the last tick whose index <= stepIndex (the most-recently
 * "completed" tier — i.e. the tier whose entirety is already visible).
 *
 * The transit screen (awizacja, screen==='transit') is NOT changed: it uses its
 * own transitDays axis (0..4) unrelated to the planning tiers.
 *
 * Also hosts the optional planning-request count toggle (bx-redo).
 *
 * Bubble-phase stopPropagation prevents pan/zoom from firing on the map beneath.
 *
 * Props
 * -----
 * stepIndex    {number}  Current discrete slider index (0..stepCount-1).
 * stepCount    {number}  Total number of steps (visibilitySteps.length).
 * trackWidth   {number}  Deadlines track width in px (visibilitySliderWidth) so
 *              adjacent tier labels keep a minimum gap regardless of cohort
 *              count. The transit screen keeps its own fixed axis (0..4).
 * onChange     {fn}      Called with next integer index when the slider moves.
 * tierTicks    {Array<{tier,color,index,percent}>}
 *              Pre-computed tick descriptors from tierStepTicks().
 * onTierStepClick {fn}  Called with tier number when a tier label is clicked.
 * markAttempts {bool}   Whether planning-request counts are shown.
 * onMarkAttemptsChange {fn}
 * screen       {'deadlines'|'transit'}
 * onScreenChange {fn}
 * transitDays  {number} 0..5 (0=off, 1=+0 today, …, 5=+4)
 * onTransitDaysChange {fn}
 */
export const PlanningVisibilitySlider = ({
  stepIndex = 0,
  stepCount = 1,
  trackWidth = null,
  onChange = () => {},
  tierTicks = [],
  onTierStepClick = () => {},
  markAttempts = false,
  onMarkAttemptsChange = () => {},
  screen = 'deadlines',
  onScreenChange = () => {},
  transitDays = 0,
  onTransitDaysChange = () => {},
}) => {
  const stop = (e) => {
    e.stopPropagation();
  };

  const isTransitScreen = screen === 'transit';

  // Fill percentage: 0% at index 0 (most urgent), 100% at last index (show-all).
  // stepCount=1 means only show-all exists; always fully filled.
  const fillPercent = stepCount > 1 ? (stepIndex / (stepCount - 1)) * 100 : 100;

  const transitPercent = Math.max(0, Math.min(MAX_TRANSIT_DAYS, transitDays)) / MAX_TRANSIT_DAYS * 100;

  // Active tick: the LAST tick whose index is <= current stepIndex.
  // This identifies the most-recently "completed" tier (all its cohorts visible).
  // No epsilon needed because we compare integer indices, not floats.
  const activeTierIndex = useMemo(() => {
    if (tierTicks.length === 0) return -1;
    let last = -1;
    for (let i = 0; i < tierTicks.length; i++) {
      if (tierTicks[i].index <= stepIndex) last = i;
    }
    return last;
  }, [tierTicks, stepIndex]);

  return (
    <div
      className="map__planning-slider"
      data-testid="planning-visibility-slider"
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={stop}
    >
      <div
        className="map__planning-slider-track"
        style={!isTransitScreen && trackWidth ? { width: `${trackWidth}px` } : undefined}
      >
        <div className="map__planning-slider-track-wrap">
          <div className="map__planning-slider-track-visual">
            <div
              className="map__planning-slider-track-fill"
              style={{ width: fillWidthStyle(isTransitScreen ? transitPercent : fillPercent) }}
            />
          </div>

          {isTransitScreen ? (
            <input
              type="range"
              className="map__planning-slider-range"
              min="0"
              max={MAX_TRANSIT_DAYS}
              step="1"
              value={transitDays}
              data-testid="planning-transit-days-range"
              aria-label="Widoczność awizacji"
              aria-valuenow={transitDays}
              aria-valuemin={0}
              aria-valuemax={MAX_TRANSIT_DAYS}
              onChange={(e) => onTransitDaysChange(Number(e.target.value))}
            />
          ) : (
            <input
              type="range"
              className="map__planning-slider-range"
              min="0"
              max={stepCount - 1}
              step="1"
              value={stepIndex}
              data-testid="planning-visibility-range"
              aria-label="Widoczność planowania"
              aria-valuenow={stepIndex}
              aria-valuemin={0}
              aria-valuemax={stepCount - 1}
              onChange={(e) => onChange(Number(e.target.value))}
            />
          )}
        </div>

        {isTransitScreen ? (
          <div
            className="map__planning-slider-scale map__planning-transit-scale"
            data-testid="planning-transit-controls"
            role="group"
            aria-label="Skala awizacji"
          >
            {[0, 1, 2, 3, 4, 5].map((days) => {
              const isActive = days === transitDays;
              const label = days === 0 ? '—' : `+${days - 1}`;
              const title = days === 0 ? 'Ukryj awizacje' : `Pokaż awizacje do ${label}`;
              return (
                <button
                  key={days}
                  type="button"
                  className={`map__planning-tier-step${isActive ? ' is-active' : ''}`}
                  style={{
                    left: `${days / MAX_TRANSIT_DAYS * 100}%`,
                    transform: 'translateX(-50%)',
                  }}
                  data-testid={`planning-transit-step-${days}`}
                  title={title}
                  aria-pressed={isActive}
                  onClick={() => onTransitDaysChange(days)}
                >
                  <span className="map__planning-tier-mark" aria-hidden="true" />
                  <span className="map__planning-tier-foot">
                    <span
                      className="map__planning-tier-tick"
                      style={{ backgroundColor: days === 0 ? '#a3adba' : '#5A8DEE' }}
                    />
                    <span className="map__planning-tier-step-label">{label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div
            className="map__planning-slider-scale"
            data-testid="planning-tier-scale"
            role="group"
            aria-label="Skala tierów planowania"
          >
            {tierTicks.map((tick, idx) => {
              // Active = this tick is the most-recently completed tier (last tick
              // whose index <= stepIndex), mirroring the old activeSegment logic
              // but in integer-index space, no float epsilon needed.
              const isActive = idx === activeTierIndex;
              return (
                <button
                  key={tick.tier}
                  type="button"
                  className={`map__planning-tier-step${isActive ? ' is-active' : ''}`}
                  style={{
                    // labelPercent is the collision-free position (spread apart
                    // when low tiers cluster on the left); fall back to the raw
                    // cohort percent if not provided. Subtask 869dxzmum.
                    left: `${tick.labelPercent != null ? tick.labelPercent : tick.percent}%`,
                    transform: 'translateX(-50%)',
                  }}
                  data-testid={`planning-tier-step-${tick.tier}`}
                  title={`Pokaż tier T${tick.tier} i pilniejsze`}
                  aria-pressed={isActive}
                  onClick={() => onTierStepClick(tick.tier)}
                >
                  <span className="map__planning-tier-mark" aria-hidden="true" />
                  <span className="map__planning-tier-foot">
                    <span
                      className="map__planning-tier-tick"
                      style={{ backgroundColor: tick.color }}
                    />
                    <span className="map__planning-tier-step-label">T{tick.tier}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        className={`map__planning-slider-mode ${isTransitScreen ? 'is-active' : ''}`}
        data-testid="planning-transit-screen-toggle"
        title={isTransitScreen ? 'Wróć do terminowości' : 'Pokaż ustawienia awizacji'}
        aria-pressed={isTransitScreen}
        onClick={() => onScreenChange(isTransitScreen ? 'deadlines' : 'transit')}
      >
        <i className="bx bx-time" />
      </button>

      {!isTransitScreen && (
        <button
          type="button"
          className={`map__planning-slider-mode ${markAttempts ? 'is-active' : ''}`}
          data-testid="planning-mark-attempts"
          title={markAttempts
            ? 'Liczba planowań: widoczna (kliknij, aby ukryć)'
            : 'Pokaż liczbę planowań zleceń'}
          aria-pressed={markAttempts}
          onClick={() => onMarkAttemptsChange(!markAttempts)}
        >
          <i className="bx bx-redo" />
        </button>
      )}
    </div>
  );
};
